import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Slider } from '@/components/ui/slider'
import { Badge } from '@/components/ui/badge'
import { ConfirmationDialog } from '@/components/confirmation-dialog'
import { PencilLine, AlertCircle, Loader, ArrowRight } from 'lucide-react'
import { useEffect, useState, ReactNode } from 'react'
import { useForm, UseFormReturn } from 'react-hook-form'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { toast } from 'react-hot-toast'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'

export type LimitField = {
  name: string
  label: string
  unit: string
  step: number
  minValue: number
  testId: string
}

type BaseOrgLimitCardProps<T extends Record<string, any>> = {
  isLoading: boolean
  currentLimits: T
  limitFields: LimitField[]
  formSchema: z.ZodType<T>
  onLimitChange?: (...limits: number[]) => void
  updateMutation: {
    mutate: (
      data: T,
      options?: { onSuccess?: () => void; onError?: (error: any) => void }
    ) => void
    isPending: boolean
  }
  confirmationConfig: {
    title: string
    description: ReactNode | ((watchedValues: T, currentLimits: T) => ReactNode)
    confirmText: string | ((watchedValues: T) => string)
    confirmPrompt: string | ((watchedValues: T) => string)
    warningPoints: string[]
  }
  editButtonLabel: string
  testIds: {
    editButton: string
    cancelButton: string
    applyButton: string
  }
}

type CardState = {
  isEditing: boolean
  isConfirming: boolean
}

export const BaseOrgLimitCard = <T extends Record<string, any>>({
  isLoading,
  currentLimits,
  limitFields,
  formSchema,
  onLimitChange,
  updateMutation,
  confirmationConfig,
  editButtonLabel,
  testIds,
}: BaseOrgLimitCardProps<T>) => {
  const [state, setState] = useState<CardState>({
    isEditing: false,
    isConfirming: false,
  })

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: currentLimits as any,
  })

  const watchedValues = form.watch()

  // Calculate percentage changes for each field
  const percentageChanges = limitFields.map((field) => {
    const current = currentLimits[field.name] as number
    const draft = watchedValues[field.name] as number

    if (current === 0 && draft === 0) return 0
    if (current === 0 && draft > 0) return 100
    if (draft === 0) return -100
    return Math.round((draft / current - 1) * 100)
  })

  useEffect(() => {
    if (!state.isEditing) {
      Object.keys(currentLimits).forEach((key) => {
        form.setValue(key as any, currentLimits[key])
      })

      if (onLimitChange) {
        const limitValues = limitFields.map(
          (field) => currentLimits[field.name] as number
        )
        onLimitChange(...limitValues)
      }
    }
  }, [currentLimits, onLimitChange, form, state.isEditing, limitFields])

  const onEditClick = () => {
    Object.keys(currentLimits).forEach((key) => {
      form.setValue(key as any, currentLimits[key])
    })
    setState((prev) => ({ ...prev, isEditing: true }))
  }

  const onCancelClick = () => {
    setState((prev) => ({ ...prev, isEditing: false }))
  }

  const onConfirmClick = () => {
    if (form.formState.isValid) {
      setState((prev) => ({ ...prev, isConfirming: true }))
    }
  }

  const onConfirmUpdate = () => {
    updateMutation.mutate(watchedValues as T, {
      onSuccess: () => {
        setState({ isEditing: false, isConfirming: false })
        toast.success('Limits updated successfully')
      },
      onError: (err) => {
        console.error('Failed to update limits:', err)
        toast.error(JSON.stringify(err))
        setState((prev) => ({ ...prev, isConfirming: false }))
      },
    })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader className="h-6 w-6 text-primary animate-spin mr-2" />
        <span>Loading limits...</span>
      </div>
    )
  }

  const { isEditing, isConfirming } = state

  return (
    <>
      <div className="relative">
        {!isEditing ? (
          <ViewModeContent
            currentLimits={currentLimits}
            limitFields={limitFields}
            onEditClick={onEditClick}
            editButtonLabel={editButtonLabel}
            testId={testIds.editButton}
          />
        ) : (
          <EditModeContent
            form={form}
            currentLimits={currentLimits}
            watchedValues={watchedValues}
            limitFields={limitFields}
            percentageChanges={percentageChanges}
            isPending={updateMutation.isPending}
            onCancelClick={onCancelClick}
            onConfirmClick={onConfirmClick}
            testIds={testIds}
          />
        )}
      </div>

      <ConfirmationDialog
        open={isConfirming}
        onOpenChange={(open) =>
          setState((prev) => ({ ...prev, isConfirming: open }))
        }
        title={confirmationConfig.title}
        description={
          typeof confirmationConfig.description === 'function'
            ? (
                confirmationConfig.description as (
                  watchedValues: T,
                  currentLimits: T
                ) => ReactNode
              )(watchedValues, currentLimits)
            : confirmationConfig.description
        }
        onConfirm={onConfirmUpdate}
        buttons={{
          confirm: {
            label: 'Confirm Update',
            isLoading: updateMutation.isPending,
          },
        }}
        confirmation={{
          text:
            typeof confirmationConfig.confirmText === 'function'
              ? (
                  confirmationConfig.confirmText as (watchedValues: T) => string
                )(watchedValues)
              : confirmationConfig.confirmText,
          prompt:
            typeof confirmationConfig.confirmPrompt === 'function'
              ? (
                  confirmationConfig.confirmPrompt as (
                    watchedValues: T
                  ) => string
                )(watchedValues)
              : confirmationConfig.confirmPrompt,
        }}
        info={{
          warningPoints: confirmationConfig.warningPoints,
        }}
      />
    </>
  )
}

type ViewModeContentProps<T> = {
  currentLimits: T
  limitFields: LimitField[]
  onEditClick: () => void
  editButtonLabel: string
  testId: string
}

const ViewModeContent = <T extends Record<string, any>>({
  currentLimits,
  limitFields,
  onEditClick,
  editButtonLabel,
  testId,
}: ViewModeContentProps<T>) => (
  <div className="space-y-6">
    <div className="flex flex-col gap-2">
      <div className="flex justify-between items-center">
        <span className="text-sm font-medium text-muted-foreground">
          Current {limitFields.length > 1 ? 'Limits' : 'Limit'}
        </span>
        <Badge variant="outline" className="px-2 py-0 text-sm">
          Default
        </Badge>
      </div>
      {limitFields.map((field) => (
        <div key={field.name} className="flex items-baseline gap-2">
          <span className="text-3xl font-bold">
            {field.name.includes('credit') ? '$' : ''}
            {currentLimits[field.name] as number}
          </span>
          <span className="text-sm text-muted-foreground">{field.unit}</span>
        </div>
      ))}
    </div>

    <Button
      variant="outline"
      onClick={onEditClick}
      className="w-fit"
      data-testid={testId}
    >
      <PencilLine className="mr-2 h-4 w-4" />
      {editButtonLabel}
    </Button>
  </div>
)

type EditModeContentProps<T> = {
  form: UseFormReturn<any>
  currentLimits: T
  watchedValues: T
  limitFields: LimitField[]
  percentageChanges: number[]
  isPending: boolean
  onCancelClick: () => void
  onConfirmClick: () => void
  testIds: { cancelButton: string; applyButton: string }
}

const EditModeContent = <T extends Record<string, any>>({
  form,
  currentLimits,
  watchedValues,
  limitFields,
  percentageChanges,
  isPending,
  onCancelClick,
  onConfirmClick,
  testIds,
}: EditModeContentProps<T>) => {
  return (
    <Form {...form}>
      <form className="space-y-6">
        <div className="space-y-6">
          {limitFields.map((field, index) => {
            const current = currentLimits[field.name] as number
            const draft = watchedValues[field.name] as number
            const percentageChange = percentageChanges[index]
            const maxSliderValue = Math.max(
              Math.ceil(current * 1.3),
              field.minValue
            )

            return (
              <div key={field.name} className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">{field.label}</span>
                  {index === 0 && (
                    <Badge variant="secondary" className="px-2 py-0 text-sm">
                      Editing
                    </Badge>
                  )}
                </div>

                <div className="flex items-center justify-between border border-border rounded-md p-3 mb-4 bg-muted/30">
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">
                      Current Limit
                    </p>
                    <p className="text-xl font-bold">
                      {field.name.includes('credit') ? '$' : ''}
                      {current}
                    </p>
                  </div>
                  <ArrowRight className="text-muted-foreground h-5 w-5" />
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">New Limit</p>
                    <p className="text-xl font-bold text-primary">
                      {field.name.includes('credit') ? '$' : ''}
                      {draft}
                    </p>
                    <Badge className="bg-primary/20 text-primary hover:bg-primary/25 border-0">
                      {percentageChange > 0 ? '+' : ''}
                      {percentageChange}%
                    </Badge>
                  </div>
                </div>

                <FormField
                  control={form.control}
                  name={field.name}
                  render={({ field: fieldData }) => (
                    <FormItem>
                      <div className="flex items-center justify-between gap-4">
                        <div className="relative w-full">
                          <FormControl>
                            <Slider
                              value={[fieldData.value]}
                              min={0}
                              max={maxSliderValue}
                              step={field.step}
                              onValueChange={(value) =>
                                fieldData.onChange(value[0])
                              }
                              className="w-full"
                            />
                          </FormControl>
                          <div className="flex justify-between mt-1 text-xs text-muted-foreground">
                            <span>
                              {field.name.includes('credit') ? '$0' : '0'}
                            </span>
                            <span>
                              {field.name.includes('credit') ? '$' : ''}
                              {maxSliderValue / 2}
                            </span>
                            <span>
                              {field.name.includes('credit') ? '$' : ''}
                              {maxSliderValue}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center">
                          {field.name.includes('credit') && (
                            <span className="text-lg mr-2">$</span>
                          )}
                          <FormControl>
                            <Input
                              type="number"
                              value={
                                fieldData.value === 0 ? '' : fieldData.value
                              }
                              onChange={(e) => {
                                const value =
                                  e.target.value === ''
                                    ? 0
                                    : Number(e.target.value)
                                fieldData.onChange(value)
                              }}
                              className="w-32 text-xl font-bold text-center"
                              min={0}
                              data-testid={field.testId}
                            />
                          </FormControl>
                        </div>
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )
          })}
        </div>

        <WarningBox />

        <div className="flex gap-3">
          <Button
            type="button"
            variant="outline"
            className="flex-1"
            onClick={onCancelClick}
            data-testid={testIds.cancelButton}
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="flex-1"
            onClick={onConfirmClick}
            disabled={isPending || !form.formState.isValid}
            data-testid={testIds.applyButton}
          >
            {isPending ? (
              <div className="flex items-center gap-2">
                <Loader className="h-4 w-4 animate-spin" />
                <span>Processing...</span>
              </div>
            ) : (
              'Apply to All Users'
            )}
          </Button>
        </div>
      </form>
    </Form>
  )
}

const WarningBox = () => (
  <div className="bg-destructive/10 border border-destructive/30 rounded-md p-3 flex gap-2">
    <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
    <div className="text-sm">
      <p className="font-medium text-destructive">Warning</p>
      <p className="text-muted-foreground">
        Updating the organization-wide limit will override all existing custom
        user limits.
      </p>
    </div>
  </div>
)
